import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  DollarSign,
  Calendar,
  AlertTriangle,
  FileText,
  Users,
  Building,
  TrendingUp,
  Upload,
  Bot,
  FileBarChart,
  Check,
  ChevronRight,
  Loader2,
  RefreshCw,
  HandCoins,
  LayoutGrid,
  Eye,
  Edit,
  Trash2,
  FileDown,
  Table as Tb,
  IndianRupee,
  Tags,
  MapPinned
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { dealService } from "@/services/dealService";
import {
  Deal,
  DealStatus,
  DealType,
  DEAL_STATUS_LABELS,
  DEAL_TYPE_LABELS,
  DealStatsResponse,
} from "@/types/deal";
import { formatDealValue } from "@/utils/formatDealValue";
import { formatFullValue } from "@/utils/formatFullValue";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrencySymbol } from "@/components/ui/currency-select";
import { AppPagination, PaginationInfo } from "@/components/ui/app-pagination";

const Deals = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const isAdminOrAnalyst = user?.role === "ADMIN" || user?.role === "ANALYST";
  const isReviewer = user?.role === "REVIEWER";
  const isViewer = user?.role === "VIEWER";
   const navigate = useNavigate()

    // Get organization's default currency and symbol
  const orgCurrency = user?.organization?.defaultCurrency || 'INR';
  const currencySymbol = useMemo(() => getCurrencySymbol(orgCurrency), [orgCurrency]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<DealStatus | "all">("all");
  const [filterType, setFilterType] = useState<DealType | "all">("all");
  const [deals, setDeals] = useState<Deal[]>([]);
  const [dealStats, setDealStats] = useState<DealStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  const { toast } = useToast();

  const loadDealStats = async () => {
    try {
      setStatsLoading(true);
      const stats = await dealService.getDealStats();
      setDealStats(stats);
    } catch (error) {
      console.error("Failed to load deal stats:", error);
      toast({
        title: "Error",
        description: "Failed to load deal statistics. Please try again.",
        variant: "destructive",
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const [pagination, setPagination] = useState<any | null>(null);


  const loadDeals = async (showRefreshLoader = false, pageSizeOverride?: number) => {
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const query = {
        page,
        limit: pageSizeOverride ?? pagination?.pageSize ?? 20,
        search: searchQuery || undefined,
        status: filterStatus !== "all" ? filterStatus : undefined,
        type: filterType !== "all" ? filterType : undefined,
        sortBy: "updatedAt" as const,
        sortOrder: "desc" as const,
      };

      const response = await dealService.getDeals(query);
      setDeals(response.deals);
setPagination(response.pagination);
    } catch (error) {
      console.error("Failed to load deals:", error);
      toast({
        title: "Error",
        description: "Failed to load deals. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDeals();
    loadDealStats();
  }, [page, filterStatus, filterType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadDeals();
      } else {
        setPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleExportDeal = async (dealId: string) => {
    try {
      await dealService.exportDeal(dealId);
      toast({
        title: "Success",
        description: "Deal exported successfully.",
      });
    } catch (error) {
      console.error("Failed to export deal:", error);
      toast({
        title: "Error",
        description: "Failed to export deal. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteDeal = async (dealId: string) => {
    try {
      await dealService.deleteDeal(dealId);
      toast({
        title: "Success",
        description: "Deal deleted successfully.",
      });
      loadDeals(true);
      loadDealStats();
    } catch (error) {
      console.error("Failed to delete deal:", error);
      toast({
        title: "Error",
        description: "Failed to delete deal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const getStatusColor = (status: DealStatus) => {
    return dealService.getStatusColor(status);
  };

  const getRiskLevel = (deal: Deal) => {
    return dealService.getRiskLevel(deal);
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "High":
        return "text-red-600";
      case "Medium":
        return "text-yellow-600";
      case "Low":
        return "text-green-600";
      default:
        return "text-gray-600";
    }
  };

  const hasCompletedReport = (deal: Deal) => {
    return deal._count?.reports && deal._count.reports > 0;
  };

  const getCurrentStep = (deal: Deal) => {
    if (hasCompletedReport(deal)) return 3;

    if (deal.status === "DRAFT") return 1;
    if (deal.status === "ACTIVE") return 1;
    if (deal.status === "DOCUMENTS_SUBMITTED") return 2;
    if (deal.status === "UNDER_REVIEW" || deal.status === "PENDING_APPROVAL")
      return 2;
    if (deal.status === "COMPLETED") return 3;
    return 1;
  };

  const calculateStepProgress = (deal: Deal) => {
    const currentStep = getCurrentStep(deal);

    switch (currentStep) {
      case 1: {
        const hasDocuments = deal._count?.documents && deal._count.documents > 0;
        if (deal.status === "DRAFT") return 0;
        if (deal.status === "ACTIVE" && hasDocuments) return 25;
        if (deal.status === "ACTIVE" && !hasDocuments) return 10;
        return 33;
      }
      case 2: {
        if (deal.status === "DOCUMENTS_SUBMITTED") return 40;
        if (deal.status === "UNDER_REVIEW") return 55;
        if (deal.status === "PENDING_APPROVAL") return 65;
        return 66;
      }
      case 3: {
        if (hasCompletedReport(deal)) return 100;
        if (deal.status === "COMPLETED") return 100;
        return 75;
      }
      default:
        return 0;
    }
  };
   const onCardClick = useCallback(
    (id: string) => {
      navigate(`/deals/${id}`);
    },
    [navigate]
  );

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-50 via-background to-gray-100/60 dark:from-gray-950 dark:via-background dark:to-gray-900/60 p-3 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1 animate-in fade-in-50">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">Deals</h1>
            <p className="text-sm sm:text-base text-muted-foreground font-light">
              Manage and track all your deal processes
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto animate-in fade-in-50 slide-in-from-right-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                loadDeals(true);
                loadDealStats();
              }}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-lg border-muted-foreground/20 hover:shadow-sm transition-colors w-full sm:w-auto"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            {isAdmin && (
              <Link to="/register-deal" className="w-full sm:w-auto">
                <Button className="flex items-center gap-2 font-medium rounded-lg shadow-[0_8px_14px_-6px_rgba(0,0,0,0.25)] hover:shadow-[0_16px_22px_-10px_rgba(0,0,0,0.3)] transition-all w-full sm:w-auto">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Register New Deal</span>
                  <span className="sm:hidden">New Deal</span>
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="group border shadow-modern-md bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/40 dark:to-slate-900/10 hover:shadow-modern-lg hover:-translate-y-0.5 transition-all duration-300 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Total Deals
              </CardTitle>
              <div className="p-2 bg-slate-500/10 rounded-lg">
                <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                {statsLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  dealStats?.total || 0
                )}
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                All deals in organization
              </p>
            </CardContent>
          </Card>

          <Card className="group border shadow-modern-md bg-gradient-to-br from-gray-50 to-white dark:from-gray-900/40 dark:to-gray-900/10 hover:shadow-modern-lg hover:-translate-y-0.5 transition-all duration-300 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active
              </CardTitle>
              <div className="p-2 bg-gray-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {statsLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  dealStats?.byStatus?.ACTIVE || 0
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Active deals
              </p>
            </CardContent>
          </Card>

          <Card className="group border shadow-modern-md bg-gradient-to-br from-zinc-50 to-white dark:from-zinc-900/40 dark:to-zinc-900/10 hover:shadow-modern-lg hover:-translate-y-0.5 transition-all duration-300 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-zinc-700 dark:text-zinc-300">Total Value</CardTitle>
              <div className="p-2 bg-zinc-500/10 rounded-lg">
                <DollarSign className="h-5 w-5 text-zinc-600 dark:text-zinc-400" />
              </div>
            </CardHeader>
            <CardContent>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger asChild>
                    <div className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 cursor-pointer">
                      {statsLoading ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          {currencySymbol} {formatDealValue(dealStats?.totalValue || 0, orgCurrency)}
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{currencySymbol}{formatFullValue(dealStats?.totalValue || 0, orgCurrency)}</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">Combined value ({orgCurrency})</p>
            </CardContent>
          </Card>

          <Card className="group border shadow-modern-md bg-gradient-to-br from-stone-50 to-white dark:from-stone-900/40 dark:to-stone-900/10 hover:shadow-modern-lg hover:-translate-y-0.5 transition-all duration-300 rounded-xl">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <CardTitle className="text-sm font-medium text-stone-700 dark:text-stone-300">
                Avg. Progress
              </CardTitle>
              <div className="p-2 bg-stone-500/10 rounded-lg">
                <Calendar className="h-5 w-5 text-stone-600 dark:text-stone-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-stone-900 dark:text-stone-100">
                {statsLoading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  `${dealStats?.averageProgress || 0}%`
                )}
              </div>
              <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">
                Overall progress
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-modern-md rounded-xl">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4 transition-opacity group-focus-within:opacity-70" />
                  <Input
                    placeholder="Search deals by name, buyer, seller, or jurisdiction..."
                    className="pl-10 font-light rounded-lg border-muted-foreground/20 focus-visible:ring-2 focus-visible:ring-primary/40 transition-shadow"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={filterStatus}
                  onValueChange={(value: DealStatus | "all") =>
                    setFilterStatus(value)
                  }
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(DEAL_STATUS_LABELS).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <Select
                  value={filterType}
                  onValueChange={(value: DealType | "all") =>
                    setFilterType(value)
                  }
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {Object.entries(DEAL_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center rounded-md border border-muted-foreground/20 overflow-hidden">
                  <Button
                    variant={viewMode === "card" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("card")}
                    className="rounded-none"
                  >
                    <LayoutGrid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === "table" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-none"
                  >
                    <Tb className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20 animate-in fade-in-50">
            <Loader2 className="w-8 h-8 animate-spin" />
            <span className="ml-2 text-muted-foreground">Loading deals...</span>
          </div>
        )}

         {/* Empty (cards) */}
        {!loading && viewMode === "card" && deals?.length === 0 && (
          <Card className="border-0 shadow-modern-md rounded-xl">
            <CardContent className="flex flex-col items-center justify-center py-14 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No deals found</h3>
              <p className="text-muted-foreground mb-4 font-light">
                {searchQuery || filterStatus !== "all" || filterType !== "all"
                  ? "Try adjusting your search terms or filters"
                  : "Get started by registering your first deal"}
              </p>
              {!searchQuery &&
                filterStatus === "all" &&
                filterType === "all" && (
                  <>
                    {isAdmin && (
                      <Link to="/register-deal">
                        <Button className="font-medium rounded-lg">
                          <Plus className="w-4 h-4 mr-2" />
                          Register First Deal
                        </Button>
                      </Link>
                    )}
                  </>
                )}
            </CardContent>
          </Card>
        )}

        {/* Cards (premium layout) */}
        {!loading && viewMode === "card" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            {deals.map((deal, idx) => (
              <Card
                key={deal.id}
                role="button"
                tabIndex={0}
                onClick={() => onCardClick(deal.id)}
                onKeyDown={(e) => e.key === "Enter" && onCardClick(deal.id)}
                className="group border-0 shadow-modern-md bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/75 hover:shadow-modern-lg transition-all duration-300 rounded-2xl hover:-translate-y-1 focus-visible:ring-2 focus-visible:ring-primary/40 outline-none animate-in fade-in-50 slide-in-from-bottom-2"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 space-y-2">
                     <CardTitle
  className="text-xl font-bold tracking-tight text-foreground/90 group-hover:text-primary transition-colors truncate"
>
  {deal.title}
</CardTitle>

                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`${getStatusColor(deal.status)} shadow-sm`}>{DEAL_STATUS_LABELS[deal.status]}</Badge>
                        <Badge variant="outline" className="font-light border-muted-foreground/30">
                          {DEAL_TYPE_LABELS[deal.type]}
                        </Badge>
                      </div>
                    </div>

                    {/* Top-right clean action icons */}
                    <div className="shrink-0 flex items-center gap-2">
  <Button
    variant="ghost"
    size="icon"
    className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-muted"
    onClick={(e) => {
      e.stopPropagation();
      handleExportDeal(deal.id);
    }}
  >
    <FileDown className="h-4 w-4" />
  </Button>

  {isAdmin && (
    <Button
      variant="ghost"
      size="icon"
      className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
      onClick={(e) => {
        e.stopPropagation();
        setConfirmDeleteId(deal.id);
      }}
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  )}
</div>

                  </div>
                </CardHeader>

                <CardContent className="space-y-5">
                  {/* Value & Expected Close */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <HandCoins className="w-4 h-4" />
                        <span className="font-light">Value</span>
                      </div>
                      <p className="font-semibold ml-1 tracking-tight">
                        <span>{getCurrencySymbol(deal.currency)}</span>
                        {dealService.formatDealValue(deal.value)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="w-4 h-4" />
                        <span className="font-light">Expected Close</span>
                      </div>
                      <p className="font-medium">{dealService.formatDate(deal.expectedClosingDate)}</p>
                    </div>
                  </div>

                  {/* Jurisdiction & Industry */}
                  {(deal.jurisdiction || deal.industry) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPinned className="w-4 h-4" />
                          <span className="font-light">Buyer Jurisdiction</span>
                        </div>
                        <p className="font-medium">{deal.jurisdiction || "Not specified"}</p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Tags className="w-4 h-4" />
                          <span className="font-light">Industry</span>
                        </div>
                        <p className="font-medium">{deal.industry || "Not specified"}</p>
                      </div>
                    </div>
                  )}

                  {/* Parties */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Users className="w-4 h-4" />
                        <span className="font-light">Buyer</span>
                      </div>
                      <p className="text-sm font-medium">{deal.buyer || "Not specified"}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <Building className="w-4 h-4" />
                        <span className="font-light">Seller</span>
                      </div>
                      <p className="text-sm font-medium">{deal.seller || "Not specified"}</p>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="font-light">Progress</span>
                      <span className="font-medium">{deal.progress}%</span>
                    </div>
                    <Progress value={deal.progress} className="w-full h-2.5 rounded-md" />
                  </div>

                  {/* Docs / Risk / Expected Close (mini) */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-sm">
                    <div className="flex items-center gap-1">
                      <AlertTriangle className={`w-4 h-4 ${getRiskColor(getRiskLevel(deal))}`} />
                      <span className="font-light">{getRiskLevel(deal)} Risk</span>
                    </div>
                    <div className="flex items-center gap-4 text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        <span className="font-light">{deal._count?.documents || 0} docs</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span className="font-light hidden sm:inline">Expected: </span>
                        <span className="font-light">{dealService.formatDate(deal.expectedClosingDate)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Stepper */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground">Due Diligence Process</span>
                      <span className="text-xs text-muted-foreground">Step {getCurrentStep(deal)} of 3</span>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto">
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <div
                          className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                            getCurrentStep(deal) >= 1 ? "bg-primary text-primary-foreground shadow" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {getCurrentStep(deal) > 1 ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : <Upload className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </div>
                        <span
                          className={`text-xs sm:text-sm font-light ${getCurrentStep(deal) >= 1 ? "text-foreground" : "text-muted-foreground"}`}
                        >
                          Docs
                        </span>
                      </div>

                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />

                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <div
                          className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                            getCurrentStep(deal) >= 2 ? "bg-primary text-primary-foreground shadow" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {getCurrentStep(deal) > 2 ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : <Bot className="w-3 h-3 sm:w-4 sm:h-4" />}
                        </div>
                        <span
                          className={`text-xs sm:text-sm font-light ${getCurrentStep(deal) >= 2 ? "text-foreground" : "text-muted-foreground"}`}
                        >
                          AI Review
                        </span>
                      </div>

                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />

                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <div
                          className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                            getCurrentStep(deal) >= 3 ? "bg-primary text-primary-foreground shadow" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          <FileBarChart className="w-3 h-3 sm:w-4 sm:h-4" />
                        </div>
                        <span
                          className={`text-xs sm:text-sm font-light ${getCurrentStep(deal) >= 3 ? "text-foreground" : "text-muted-foreground"}`}
                        >
                          Report
                        </span>
                      </div>
                    </div>

                    <div className="mt-4">
                      {getCurrentStep(deal) === 1 && isAdminOrAnalyst && (
                        <Link to={`/document-upload?dealId=${deal.id}`} className="w-full" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" className="w-full font-medium rounded-lg hover:shadow-sm transition-shadow">
                            <Upload className="w-4 h-4 mr-2" />
                            Upload Documents
                          </Button>
                        </Link>
                      )}
                      {getCurrentStep(deal) === 2 && (
                        <Link to={`/ai-review?dealId=${deal.id}`} className="w-full" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" className="w-full font-medium rounded-lg hover:shadow-sm transition-shadow">
                            <Bot className="w-4 h-4 mr-2" />
                            {isAdminOrAnalyst
                              ? "Start AI Review"
                              : isReviewer
                              ? "Review AI Review"
                              : "View AI Review"}
                          </Button>
                        </Link>
                      )}
                      {getCurrentStep(deal) === 3 && (
                        <Link to={`/report-generation?dealId=${deal.id}`} className="w-full" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" className="w-full font-medium rounded-lg hover:shadow-sm transition-shadow">
                            <FileBarChart className="w-4 h-4 mr-2" />
                            {hasCompletedReport(deal) ? "View Report" : "Generate Report"}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Table */}
        {!loading && viewMode === "table" && (
          <Card className="border-0 shadow-modern-md rounded-xl">
            <CardContent className="p-0">
              {deals.length === 0 ? (
                <Card className="border-0 shadow-none">
                  <CardContent className="flex flex-col items-center justify-center py-14">
                    <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No deals found</h3>
                    <p className="text-muted-foreground text-center mb-4 font-light">
                      {searchQuery || filterStatus !== "all" || filterType !== "all"
                        ? "Try adjusting your search terms or filters"
                        : "Get started by registering your first deal"}
                    </p>
                    {!searchQuery &&
                      filterStatus === "all" &&
                      filterType === "all" && (
                        <>
                          {isAdmin && (
                            <Link to="/register-deal">
                              <Button className="font-medium rounded-lg">
                                <Plus className="w-4 h-4 mr-2" />
                                Register First Deal
                              </Button>
                            </Link>
                          )}
                        </>
                      )}
                  </CardContent>
                </Card>
              ) : (
                <div className="overflow-x-auto table-responsive">
                  <div className="min-w-full">
                    <Table className="rounded-xl min-w-[800px]">
                      <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="font-medium">Deal</TableHead>
                          <TableHead className="font-medium">Status</TableHead>
                          <TableHead className="font-medium">Type</TableHead>
                          <TableHead className="font-medium">Value</TableHead>
                          <TableHead className="font-medium">Buyer</TableHead>
                          <TableHead className="font-medium">Seller</TableHead>
                          <TableHead className="font-medium">Progress</TableHead>
                          <TableHead className="font-medium">Risk</TableHead>
                          <TableHead className="font-medium">Documents</TableHead>
                          <TableHead className="font-medium">Expected Close</TableHead>
                          <TableHead className="font-medium">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deals.map((deal, idx) => (
                          <TableRow
                            key={deal.id}
                            className={`transition-colors hover:bg-muted/60 cursor-pointer ${
                              idx % 2 === 0 ? "bg-transparent" : "bg-muted/30"
                            }`}
                            onClick={() => navigate(`/deals/${deal.id}`)}
                          >
                            <TableCell className="align-middle">
                              <div className="font-medium">{deal.title}</div>
                              <div className="text-xs text-muted-foreground font-light flex items-center gap-2 mt-1">
                                {deal.jurisdiction && (
                                  <span className="inline-flex items-center gap-1">
                                    <MapPinned className="w-3.5 h-3.5" />
                                    {deal.jurisdiction}
                                  </span>
                                )}
                                {deal.industry && (
                                  <span className="inline-flex items-center gap-1">
                                    <Tags className="w-3.5 h-3.5" />
                                    {deal.industry}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="align-middle">
                              <Badge className={`${getStatusColor(deal.status)} shadow-sm`}>
                                {DEAL_STATUS_LABELS[deal.status]}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-middle">
                              <Badge variant="outline" className="font-light border-muted-foreground/30">
                                {DEAL_TYPE_LABELS[deal.type]}
                              </Badge>
                            </TableCell>
                            <TableCell className="align-middle">
                              <span className="font-medium">
                                {getCurrencySymbol(deal.currency)}
                                {dealService.formatDealValue(deal.value)}
                              </span>
                            </TableCell>
                            <TableCell className="align-middle">
                              <span className="font-light">{deal.buyer || "Not specified"}</span>
                            </TableCell>
                            <TableCell className="align-middle">
                              <span className="font-light">{deal.seller || "Not specified"}</span>
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-2">
                                <Progress value={calculateStepProgress(deal)} className="w-16 h-2 rounded" />
                                <span className="text-sm font-medium">{calculateStepProgress(deal)}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-1">
                                <AlertTriangle className={`w-4 h-4 ${getRiskColor(getRiskLevel(deal))}`} />
                                <span className="font-light text-sm">{getRiskLevel(deal)}</span>
                              </div>
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-1">
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="font-light text-sm">{deal._count?.documents || 0}</span>
                              </div>
                            </TableCell>
                            <TableCell className="align-middle">
                              <span className="font-light text-sm">
                                {dealService.formatDate(deal.expectedClosingDate)}
                              </span>
                            </TableCell>
                            <TableCell className="align-middle">
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 rounded-full hover:bg-muted"
                                  aria-label="Export deal"
                                  title="Export"
                                  onClick={() => handleExportDeal(deal.id)}
                                >
                                  <FileDown className="h-4 w-4" />
                                </Button>
                                {isAdmin && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                                    aria-label="Delete deal"
                                    title="Delete"
                                    onClick={() => setConfirmDeleteId(deal.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {!loading && deals.length > 0 && pagination && (
 <AppPagination
  pagination={{
    page: pagination.page,
    pageSize: pagination.limit,
    total: pagination.total,
    totalPages: pagination.totalPages,
    hasNext: pagination.hasNext,
    hasPrev: pagination.hasPrev,
  }}
  onPageChange={(newPage) => setPage(newPage)}
  onPageSizeChange={(size) => {
    setPagination((prev: any) => ({ ...prev, pageSize: size }));
    setPage(1);
    loadDeals(false, size);  
  }}
  showFirstLast
  showPageNumbers
  showInfo
  showPageSize={true}
  pageSizeOptions={[10, 20, 50]}
/>
)}

      </div>

      <ConfirmationDialog
        open={!!confirmDeleteId}
        title="Confirm Delete"
        description="Are you sure you want to delete this deal? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={() => handleDeleteDeal(confirmDeleteId!)}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
};

export default Deals;